import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import Icon from '../../common/Icon/Icon';
import './EmailFlows.css';

/** Landing page for the verification link in the signup email. */
export function VerifyEmail() {
    const [params] = useSearchParams();
    const [status, setStatus] = useState('working'); // working | ok | error
    const [error, setError] = useState('');

    useEffect(() => {
        const token = params.get('token');
        if (!token) { setStatus('error'); setError('Missing verification token.'); return; }
        fetch('/api/auth/verify-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token }),
        })
            .then(async r => {
                const d = await r.json();
                if (!r.ok) throw new Error(d.error || 'Verification failed.');
                setStatus('ok');
            })
            .catch(e => { setStatus('error'); setError(e.message); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="emailflow-shell">
            <div className="emailflow-card">
                {status === 'working' && <p>Verifying your email&hellip;</p>}
                {status === 'ok' && (
                    <>
                        <div className="emailflow-emoji ok"><Icon name="check-circle" size={52} /></div>
                        <h2>Email verified</h2>
                        <p>Your account is confirmed. Happy trading!</p>
                        <Link className="cta-gold emailflow-btn" to="/">Go to TradeSpot</Link>
                    </>
                )}
                {status === 'error' && (
                    <>
                        <div className="emailflow-emoji warn"><Icon name="alert" size={52} /></div>
                        <h2>Verification failed</h2>
                        <p>{error}</p>
                        <p className="emailflow-hint">You can request a fresh link from Account Settings.</p>
                    </>
                )}
            </div>
        </div>
    );
}

/** Landing page for the reset link in the forgot-password email. */
export function ResetPassword() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get('token');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState('');

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        if (password !== confirm) return setError('Passwords do not match.');
        setBusy(true);
        try {
            const res = await fetch('/api/auth/reset-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, password }),
            });
            const d = await res.json();
            if (!res.ok) throw new Error(d.error || 'Reset failed.');
            setDone(true);
            setTimeout(() => navigate('/auth'), 2500);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    if (!token) {
        return (
            <div className="emailflow-shell">
                <div className="emailflow-card">
                    <h2>Invalid link</h2>
                    <p>This reset link is missing its token. Request a new one from the login page.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="emailflow-shell">
            <div className="emailflow-card">
                {done ? (
                    <>
                        <div className="emailflow-emoji ok"><Icon name="check-circle" size={52} /></div>
                        <h2>Password updated</h2>
                        <p>All devices were logged out. Taking you to the login page&hellip;</p>
                    </>
                ) : (
                    <>
                        <h2>Choose a new password</h2>
                        <form onSubmit={submit} className="emailflow-form">
                            <input
                                type="password"
                                placeholder="New password"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                required
                            />
                            <input
                                type="password"
                                placeholder="Confirm new password"
                                value={confirm}
                                onChange={e => setConfirm(e.target.value)}
                                required
                            />
                            <p className="emailflow-hint">8+ characters, with a number and a special character.</p>
                            <button className="cta-gold emailflow-btn" type="submit" disabled={busy}>
                                {busy ? 'Saving…' : 'Set new password'}
                            </button>
                            {error && <p className="emailflow-error">{error}</p>}
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
